import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Dimensions,
    FlatList,
    Image,
    Keyboard,
    Modal,
    Text,
    TouchableOpacity,
    TouchableHighlight,
    TouchableWithoutFeedback,
    View,
    ViewStyle,
    KeyboardEvent,
    I18nManager, Platform,
    ScrollView
} from 'react-native';
import CInput from '../TextInput';
import {useDeviceOrientation} from '../../useDeviceOrientation';
import {useDetectDevice} from '../../toolkits';
import {styles} from './styles';
import type {DropdownProps} from './model';
import _ from 'lodash';
//import PathFile from '../../../../../assets/PathFile';
import HMModal from '../../../../modal/HMModal';
import HMTextView from "../../../../HMTextView";
import HMIcon from "../../../../HMIcon";
import Colors from "../../../../../tools/Colors";
import {moderateScale} from "../../../../../tools/Scaling";

const {isTablet, isIOS} = useDetectDevice;
//const ic_down = (PathFile.image.logo);

const defaultProps = {
    placeholder: 'Select item',
    activeColor: '#F6F7F8',
    data: [],
    style: {},
    selectedTextProps: {},
};

const DropdownComponent = React.forwardRef<any, DropdownProps>(
    (props, currentRef) => {
        const orientation = useDeviceOrientation();
        const {
            testID,
            itemTestIDField,
            onChange,
            style,
            containerStyle,
            placeholderStyle,
            selectedTextStyle,
            inputSearchStyle,
            iconStyle,
            selectedTextProps,
            data,
            labelField,
            valueField,
            value,
            activeColor,
            fontFamily,
            iconColor = 'gray',
            searchPlaceholder,
            placeholder,
            search = false,
            maxHeight = 340,
            disable = false,
            keyboardAvoiding = true,
            renderLeftIcon,
            renderRightIcon,
            renderItem,
            renderInputSearch,
            onFocus,
            onBlur,
            autoScroll = true,
            showsVerticalScrollIndicator = true,
            dropdownPosition = 'auto',
            flatListProps,
            searchQuery,
            statusBarIsTranslucent,
            backgroundColor,
        } = props;

        const ref = useRef<View>(null);
        const refList = useRef<FlatList>(null);
        const [visible, setVisible] = useState<boolean>(false);
        const [currentValue, setCurrentValue] = useState<any>(null);
        const [listData, setListData] = useState<any[]>(data);
        const [position, setPosition] = useState<any>();
        const [focus, setFocus] = useState<boolean>(false);
        const [visibleModal, setVisibleModal] = useState<boolean>(false);
        const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

        const {width: W, height: H} = Dimensions.get('window');
        const styleContainerVertical: ViewStyle = useMemo(() => {
            return {
                backgroundColor: 'rgba(0,0,0,0.1)',
                alignItems: 'center',
            };
        }, []);
        const styleHorizontal: ViewStyle = useMemo(() => {
            return {
                marginBottom: 1,
                width: W / 1,
                alignSelf: 'center',
            };
        }, [W]);

        useImperativeHandle(currentRef, () => {
            return {open: eventOpen, close: eventClose};
        });

        useEffect(() => {
            setListData([...data]);
        }, [data]);

        const eventOpen = () => {
            if (!disable) {
                setVisible(true);
                if (onFocus) {
                    onFocus();
                }
            }
        };

        const eventClose = useCallback(() => {
            if (!disable) {
                setVisible(false);
                if (onBlur) {
                    onBlur();
                }
            }
        }, [disable, onBlur]);

        const font = useCallback(() => {
            if (fontFamily) {
                return {
                    fontFamily: fontFamily,
                };
            } else {
                return {};
            }
        }, [fontFamily]);

        const _measure = useCallback(() => {
            if (ref && ref?.current) {
                ref.current.measure((_width, _height, px, py, fx, fy) => {
                    const isFull = orientation === 'LANDSCAPE' && !isTablet;
                    const w = Math.floor(px);
                    const top = isFull ? 20 : Math.floor(py) + Math.floor(fy) + 2;
                    //      const bottom = H - top;
                    const bottom = H;
                    const left = I18nManager.isRTL
                        ? W - Math.floor(px) - Math.floor(fx)
                        : Math.floor(fx);

                    setPosition({
                        isFull,
                        w,
                        top,
                        bottom: Math.floor(bottom),
                        left,
                        height: Math.floor(py),
                    });
                });
            }
        }, [H, W, orientation]);

        const onKeyboardDidShow = useCallback(
            (e: KeyboardEvent) => {
                _measure();
                setKeyboardHeight(e.endCoordinates.height);
            },
            [_measure]
        );

        const onKeyboardDidHide = () => {
            setKeyboardHeight(0);
        };

        useEffect(() => {
            const susbcriptionKeyboardDidShow = Keyboard.addListener(
                'keyboardDidShow',
                onKeyboardDidShow
            );
            const susbcriptionKeyboardDidHide = Keyboard.addListener(
                'keyboardDidHide',
                onKeyboardDidHide
            );

            return () => {
                if (typeof susbcriptionKeyboardDidShow?.remove === 'function') {
                    susbcriptionKeyboardDidShow.remove();
                } else {
                    Keyboard.removeListener('keyboardDidShow', onKeyboardDidShow);
                }

                if (typeof susbcriptionKeyboardDidHide?.remove === 'function') {
                    susbcriptionKeyboardDidHide.remove();
                } else {
                    Keyboard.removeListener('keyboardDidHide', onKeyboardDidHide);
                }
            };
        }, [onKeyboardDidShow]);

        const getValue = useCallback(() => {
            const getItem = data.filter((e) =>
                _.isEqual(value, _.get(e, valueField))
            );
            if (getItem.length > 0) {
                setCurrentValue(getItem[0]);
            } else {
                setCurrentValue(null);
            }
        }, [data, value, valueField]);

        useEffect(() => {
            getValue();
        }, [value, data, getValue]);

        const scrollIndex = useCallback(() => {
            if (autoScroll && data.length > 0 && listData.length === data.length) {
                setTimeout(() => {
                    if (refList && refList?.current) {
                        const index = _.findIndex(listData, (e: any) =>
                            _.isEqual(value, _.get(e, valueField))
                        );
                        if (index > -1 && index <= listData.length - 1) {
                            refList?.current?.scrollToIndex({
                                index: index,
                                animated: false,
                            });
                        }
                    }
                }, 200);
            }
        }, [autoScroll, data.length, listData, value, valueField]);

        const showOrClose = useCallback(() => {
            if (!disable) {
                _measure();
                setVisible(!visible);
                setListData(data);

                if (!visible) {
                    if (onFocus) {
                        onFocus();
                    }
                } else {
                    if (onBlur) {
                        onBlur();
                    }
                }
            }
            scrollIndex();
        }, [_measure, data, disable, onBlur, onFocus, scrollIndex, visible]);

        const onSearch = useCallback(
            (text: string) => {
                if (text.length > 0) {
                    const defaultFilterFunction = (e: any) => {
                        const item = _.get(e, labelField)
                            ?.toLowerCase()
                            .replace(' ', '')
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '');
                        const key = text
                            .toLowerCase()
                            .replace(' ', '')
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '');

                        return item.indexOf(key) >= 0;
                    };

                    const propSearchFunction = (e: any) => {
                        const labelText = _.get(e, labelField);

                        return searchQuery?.(text, labelText);
                    };

                    const dataSearch = data.filter(
                        searchQuery ? propSearchFunction : defaultFilterFunction
                    );
                    setListData(dataSearch);
                } else {
                    setListData(data);
                }
            },
            [data, labelField, searchQuery]
        );

        const onSelect = useCallback(
            (item: any) => {
                onSearch('');
                setCurrentValue(item);
                onChange(item);
                eventClose();
            },
            [eventClose, onChange, onSearch]
        );

        const _renderDropdown = () => {

            const isSelected = currentValue && _.get(currentValue, valueField);
            return (
                <TouchableWithoutFeedback testID={testID} onPress={showOrClose}>
                    <View style={styles.dropdown}>
                        {false && renderLeftIcon?.()}
                        {/*        <Text
                            style={[
                                styles.textItem,
                                isSelected !== null ? selectedTextStyle : placeholderStyle,
                                font(),
                            ]}
                            {...selectedTextProps}
                        >
                            {isSelected !== null
                                ? _.get(currentValue, labelField)
                                : placeholder}
                        </Text>*/}
                        <HMTextView
                            style={[
                                styles.textItem,
                                isSelected !== null ? selectedTextStyle : placeholderStyle,
                                font(),
                                {
                                    color: "#070707"
                                }
                            ]}
                            {...selectedTextProps}
                            // text={isSelected !== null
                            //     ? _.get(currentValue, labelField)
                            //     : placeholder}
                            text={props.value}
                        />
                        {renderRightIcon ? (
                            renderRightIcon()
                        ) : (
                            <HMIcon
                                name={"chevron-down-outline"}
                                // type={"MaterialCommunityIcons"}
                                style={{
                                    alignSelf: 'center',
                                    // width: moderateScale(14),
                                    color: Colors.colorDark,
                                    marginHorizontal: moderateScale(5),
                                    fontSize: Platform.OS === 'ios' ? moderateScale(28) : moderateScale(26),
                                    textAlign: 'center'
                                }}/>
                        )}
                    </View>
                </TouchableWithoutFeedback>
            );
        };

        const _renderItem = useCallback(
            ({item, index}: { item: any; index: number }) => {
                const isSelected = currentValue && _.get(currentValue, valueField);
                const selected = _.isEqual(_.get(item, valueField), isSelected);
                return (
                    <TouchableOpacity
                        testID={_.get(item, itemTestIDField || labelField)}
                        key={index}
                        //onPress={() => onSelect(item)}

                        // onPress={(e) => {
                        //     console.log(e, e.nativeEvent)
                        // }}
                        onLongPress={(e) => {
                            onSelect(item)
                        }}
                        delayLongPress={45}
                        // accessibilityRole="link"
                        style={[
                            selected && {
                                backgroundColor: activeColor,

                            },
                            {width: W - moderateScale(10)}
                        ]}

                    >
                        <View style={{
                            flex: 1,
                            // flexDirection: "row",
                            // alignItems: "center",
                            // paddingHorizontal: 15,
                            //  backgroundColor: "#FFF"
                        }}


                        >
                            {renderItem ? (
                                renderItem(item, selected)
                            ) : (
                                <View style={[styles.item]}>
                                    <HMTextView style={[styles.textItem, font(), {paddingHorizontal: moderateScale(6)}]}
                                                text={_.get(item, labelField)}

                                    >

                                    </HMTextView>
                                </View>
                            )}
                            <View style={{
                                width: "98%",
                                height: moderateScale(1),
                                backgroundColor: "#d3d3d3",
                                alignSelf: "center"
                            }}/>
                        </View>
                    </TouchableOpacity>
                );
            },
            [
                activeColor,
                currentValue,
                font,
                itemTestIDField,
                labelField,
                onSelect,
                renderItem,
                valueField,
            ]
        );

        const renderSearch = useCallback(() => {
            if (search) {
                if (renderInputSearch) {
                    return renderInputSearch((text) => {
                        onSearch(text);
                    });
                } else {
                    return (
                        <CInput
                            testID={testID + ' input'}
                            style={[styles.input, inputSearchStyle]}
                            inputStyle={[inputSearchStyle, font()]}
                            autoCorrect={false}
                            keyboardType={isIOS ? 'default' : 'visible-password'}
                            placeholder={searchPlaceholder}
                            onChangeText={onSearch}
                            placeholderTextColor="gray"
                            iconStyle={[{tintColor: iconColor}, iconStyle]}
                            onFocus={() => setFocus(true)}
                            onBlur={() => {
                                setFocus(false);
                            }}
                        />
                    );
                }
            }
            return null;
        }, [
            font,
            iconColor,
            iconStyle,
            inputSearchStyle,
            onSearch,
            renderInputSearch,
            search,
            searchPlaceholder,
            testID,
        ]);

        const _renderListTop = useCallback(() => {
            return (
                <TouchableWithoutFeedback>
                    <View style={styles.flexShrink}>
                        <FlatList
                            testID={testID + ' flatlist'}
                            {...flatListProps}
                            keyboardShouldPersistTaps="handled"
                            ref={refList}
                            onScrollToIndexFailed={scrollIndex}
                            data={listData}
                            inverted
                            renderItem={_renderItem}
                            keyExtractor={(_item, index) => index.toString()}
                            showsVerticalScrollIndicator={showsVerticalScrollIndicator}
                        />
                        {renderSearch()}
                    </View>
                </TouchableWithoutFeedback>
            );
        }, [
            _renderItem,
            flatListProps,
            listData,
            renderSearch,
            scrollIndex,
            showsVerticalScrollIndicator,
            testID,
        ]);
        /*    const Row = ({ href, text }) => {
                return (
                    <TouchableHighlight
                        accessibilityRole="link"
                        href={href}
                        onPress={() => {
                            Linking.openURL(href);
                        }}
                        style={styles.row}
                    >
                        <View style={styles.rowInner}>
                            <Text>{text}</Text>
                        </View>
                    </TouchableHighlight>
                );
            };*/
        const _renderListBottom = useCallback(() => {
            return (
                <TouchableWithoutFeedback>
                    <View

                        style={styles.flexShrink}
                        //  style={{backgroundColor:"#89420c",width:100}}
                    >
                        {renderSearch()}


                        {/*        <ScrollView style={{flex:1}}>
                            {listData.map((item,index) => {
                            return  _renderItem({item,index})
                            })}
                        </ScrollView>*/}
                        <FlatList
                            testID={testID + ' flatlist'}
                            {...flatListProps}
                            keyboardShouldPersistTaps="handled"
                            ref={refList}
                            onScrollToIndexFailed={scrollIndex}
                            data={listData}
                            renderItem={_renderItem}
                            keyExtractor={(_item, index) => index.toString()}
                            showsVerticalScrollIndicator={showsVerticalScrollIndicator}
                        />
                    </View>
                </TouchableWithoutFeedback>
            );
        }, [
            _renderItem,
            flatListProps,
            listData,
            renderSearch,
            scrollIndex,
            showsVerticalScrollIndicator,
            testID,
        ]);

        const _renderModal = useCallback(() => {
            if (visible && position) {
                const {isFull, w, top, bottom, left, height} = position;
                if (w && top && bottom) {
                    const styleVertical: ViewStyle = {left: left, maxHeight: maxHeight};
                    const isTopPosition =
                        dropdownPosition === 'auto'
                            ? bottom < (isIOS ? 200 : search ? 310 : 300)
                            : dropdownPosition === 'top'
                            ? true
                            : false;
                    let topHeight = isTopPosition ? top - height : top;

                    let keyboardStyle: ViewStyle = {};

                    if (keyboardAvoiding) {
                        if (renderInputSearch) {
                            if (keyboardHeight > 0 && bottom < keyboardHeight + height) {
                                if (isTopPosition) {
                                    topHeight = H - keyboardHeight;
                                } else {
                                    keyboardStyle = {backgroundColor: 'rgba(0,0,0,0.1)'};
                                    topHeight = H - keyboardHeight - 55;
                                }
                            }
                        } else {
                            if (
                                focus &&
                                keyboardHeight > 0 &&
                                bottom < keyboardHeight + height
                            ) {
                                if (isTopPosition) {
                                    topHeight = H - keyboardHeight;
                                } else {
                                    keyboardStyle = {backgroundColor: 'rgba(0,0,0,0.1)'};
                                    topHeight = H - keyboardHeight - 55;
                                }
                            }
                        }
                    }

                    return (
                        <HMModal
                            // style={{backgroundColor: Colors.transparent}}
                            // position={"end"}
                            // ref={"modal"}
                            // backdropPressToClose={true}
                            // coverScreen={true}
                            // swipeToClose={true}
                            // backButtonClose={true}
                            // isOpen={this.state.visibleModal}
                            // onClosed={() => this.setState({visibleModal: false})}

                            isVisible={visible}
                            //visible={this.state.visibleModal}
                            backdropColor={"#787878"}
                            // backdropColor={"transparent"}
                            backdropOpacity={0.8}
                            animationIn="zoomInDown"
                            animationOut="zoomOutUp"
                            animationInTiming={600}
                            animationOutTiming={600}
                            backdropTransitionInTiming={600}
                            backdropTransitionOutTiming={600}
                            //style={{height: '60%'}}
                            onBackdropPress={showOrClose}
                            onBackButtonPress={showOrClose}

                            animationType="slide"


                            // transparent
                            // statusBarTranslucent={statusBarIsTranslucent}
                            // visible={visible}
                            // supportedOrientations={['landscape', 'portrait']}
                            // onRequestClose={showOrClose}
                        >


                            <View
                                style={{
                                    width: '100%',
                                    height: Platform.OS === "web" ? '60%' : "100%",
                                    // height: maxHeight,
                                    // alignItems: 'flex-end',
                                    // justifyContent: 'flex-end',
                                    //    backgroundColor: 'rgba(150, 150, 150, 0)',
                                    position: "absolute",
                                    bottom: 0,

                                }}>
                                <TouchableWithoutFeedback onPress={showOrClose}>
                                    <View
                                        style={[
                                            styles.flex1,
                                            isFull && styleContainerVertical,
                                            backgroundColor && {backgroundColor: backgroundColor},
                                            keyboardStyle,
                                            {backgroundColor: "transparent"}
                                        ]}
                                    >
                                        {
                                            isTopPosition ?
                                                <View
                                                    style={[
                                                        styles.wrapTop,
                                                        {
                                                            height: topHeight,
                                                            width: "100%",

                                                        },
                                                    ]}
                                                >
                                                    {isTopPosition && (
                                                        <View
                                                            style={[
                                                                {width: "100%",},
                                                                styles.container,
                                                                containerStyle,
                                                                // isFull ? styleHorizontal : styleVertical,
                                                            ]}
                                                        >
                                                            {/*
                                                            {_renderListTop()}
*/}
                                                            {_renderListBottom()}
                                                        </View>
                                                    )}
                                                </View>
                                                :
                                                <View style={[
                                                    //   styles.flex1,
                                                    {
                                                        flex: 1,
                                                        // alignItems: 'flex-end',
                                                        justifyContent: 'flex-end',
                                                        //   backgroundColor:"#00615c",
                                                    }]}>
                                                    {!isTopPosition && (
                                                        <View
                                                            style={[
                                                                {
                                                                    flex: 1,
                                                                    //  backgroundColor:"#610007"
                                                                },
                                                                styles.container,
                                                                containerStyle,
                                                                // isFull ? styleHorizontal : styleVertical,
                                                            ]}
                                                        >
                                                            {_renderListBottom()}
                                                        </View>
                                                    )}
                                                </View>
                                        }


                                    </View>
                                </TouchableWithoutFeedback>
                            </View>
                        </HMModal>


                    );
                }
                return null;
            }
            return null;
        }, [
            visible,
            position,
            maxHeight,
            dropdownPosition,
            search,
            keyboardAvoiding,
            statusBarIsTranslucent,
            showOrClose,
            styleContainerVertical,
            backgroundColor,
            containerStyle,
            styleHorizontal,
            _renderListTop,
            _renderListBottom,
            renderInputSearch,
            keyboardHeight,
            H,
            focus,
        ]);

        return (
            <View
                //  style={[style]}
                style={[styles.mainWrap, style]}
                ref={ref}
                onLayout={_measure}
            >


                {_renderDropdown()}

                {_renderModal()}


            </View>
        );
    }
);

DropdownComponent.defaultProps = defaultProps;

export default DropdownComponent;
